// Blast — the PB/PEG buyer blast manager (routes/blast_board.py).
// Queue → build → send → record, replacing the DealCloud-tag → bookworm →
// copy/paste-from-ipro.rest flow. Any editor queues a company from its
// record page (window.BlastQueueControl below); this page is the manager
// surface: admins + the marketing role + BLAST_MANAGER_EMAILS.
//
// Send paths, both first-class:
//   * Push to SuperSend — writes the built email into the broadcast
//     campaign; the webhook's first `sent` event auto-confirms the batch.
//   * Copy HTML + Mark as sent — Jake's manual path, kept as the fallback.

const BLAST_LISTS = [
  { key: 'pb',  label: 'PB Broadcast' },
  { key: 'peg', label: 'PEG Broadcast' },
];

const blastMoney = (v) => {
  if (v == null || !isFinite(v) || !v) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${+(v / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${+(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${Math.round(v / 1e3)}K`;
  return `$${v}`;
};

const blastDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const blastWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const BlastBtn = ({ onClick, children, tone = 'default', disabled, title }) => (
  <button onClick={onClick} disabled={disabled} title={title} style={{
    fontSize: 12, padding: '5px 10px', borderRadius: 6, cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1, fontWeight: 500,
    border: '1px solid ' + (tone === 'primary' ? 'var(--accent-text)' : 'var(--border)'),
    background: tone === 'primary' ? 'var(--accent-text)' : 'var(--surface)',
    color: tone === 'primary' ? '#fff' : tone === 'danger' ? 'var(--rose-fg, #b42318)' : 'var(--text-1)',
  }}>{children}</button>
);

const BlastStat = ({ label, value, tone }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
                padding: '8px 12px', minWidth: 90 }}>
    <div style={{ fontSize: 20, fontWeight: 600, color: tone || 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</div>
  </div>
);

const MissingChips = ({ missing }) => (
  !missing || !missing.length ? (
    <span style={{ fontSize: 11, color: 'var(--green-fg, #067647)' }}>ready</span>
  ) : (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {missing.map((m) => (
        <span key={m} style={{ fontSize: 10, color: 'var(--rose-fg, #b42318)',
          border: '1px solid var(--rose-fg, #b42318)', borderRadius: 4, padding: '0 4px',
          opacity: 0.85 }}>{m}</span>
      ))}
    </span>
  )
);

// ── Queue tab ───────────────────────────────────────────────────────────────

const BlastPastSends = ({ rows, onOpenCompany }) => {
  const [open, setOpen] = React.useState(false);
  if (!rows || !rows.length) return null;
  const th = { textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em',
               color: 'var(--text-3)', fontWeight: 500, padding: '6px 10px', whiteSpace: 'nowrap' };
  const td = { fontSize: 12, color: 'var(--text-2)', padding: '7px 10px', whiteSpace: 'nowrap' };
  return (
    <div style={{ marginTop: 14 }}>
      <button onClick={() => setOpen(!open)} style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        fontSize: 12, fontWeight: 600, color: 'var(--text-3)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
        <span style={{ display: 'inline-block', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }}>▸</span>
        Past sends ({rows.length})
      </button>
      {open && (
        <div style={{ overflowX: 'auto', marginTop: 6, opacity: 0.85 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={th}>Company</th><th style={th}>Queued</th>
              <th style={th}>In SuperSend</th><th style={th}>Email sent</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.company_id + i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={td}>
                    <button onClick={() => onOpenCompany(r.company_id)} style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: 'var(--accent-text)', fontSize: 12, textAlign: 'left' }}>{r.name}</button>
                    {r.source === 'auto_pb' && <span style={{ fontSize: 10, color: 'var(--text-4)', marginLeft: 6 }}>auto after PB</span>}
                  </td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{blastWhen(r.queued_at)}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{r.pushed_at ? blastWhen(r.pushed_at) : '—'}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                    {blastWhen(r.sent_at)}{r.sent_via ? ` (${r.sent_via === 'webhook' ? 'SuperSend confirmed' : 'marked manually'})` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const BlastQueueTab = ({ data, onOpenCompany, onRemove }) => {
  const entries = data?.entries || [];
  if (!entries.length) {
    return <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-3)' }}>
      <div>Nothing queued. Queue a company from its record page — the “Send on blast” control next to the sync badge.</div>
      <BlastPastSends rows={data?.past_sends} onOpenCompany={onOpenCompany} />
    </div>;
  }
  const th = { textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em',
               color: 'var(--text-3)', fontWeight: 500, padding: '6px 10px', whiteSpace: 'nowrap' };
  const td = { fontSize: 12, color: 'var(--text-2)', padding: '7px 10px', verticalAlign: 'top' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>
          <th style={th}>Company</th><th style={th}>Industry</th><th style={th}>Revenue</th>
          <th style={th}>EBITDA</th><th style={th}>State</th><th style={th}>RM</th>
          <th style={th}>Status</th><th style={th}>Queued</th>
          <th style={th}>In SuperSend</th><th style={th}></th>
        </tr></thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.entry_id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={td}>
                <button onClick={() => onOpenCompany(e.company_id)} style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, textAlign: 'left' }}>
                  {e.company?.name || '—'}
                </button>
                {e.source === 'dc_tag' && <span style={{ fontSize: 10, color: 'var(--text-4)', marginLeft: 6 }}>via DC tag</span>}
              </td>
              <td style={td}>{e.company?.industry || '—'}</td>
              <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{blastMoney(e.company?.revenue)}</td>
              <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{blastMoney(e.company?.ebitda)}</td>
              <td style={td}>{e.company?.state || '—'}</td>
              <td style={td}>{e.company?.rm_name || '—'}</td>
              <td style={td}>
                {e.pushed_at
                  ? <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent-text)' }}>in SuperSend</span>
                  : <MissingChips missing={e.missing} />}
              </td>
              <td style={td} title={e.queued_at || ''}>
                {blastWhen(e.queued_at)}
                {e.source === 'auto_pb' ? ' · auto after PB' : e.queued_by ? ` · ${e.queued_by}` : ''}
              </td>
              <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{e.pushed_at ? blastWhen(e.pushed_at) : '—'}</td>
              <td style={td}>
                <BlastBtn tone="danger" title="Remove from this blast queue"
                  onClick={() => onRemove(e)}>Remove</BlastBtn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <BlastPastSends rows={data?.past_sends} onOpenCompany={onOpenCompany} />
    </div>
  );
};

// ── Email tab ───────────────────────────────────────────────────────────────

// Who the next push holds out of the broadcast (GET /api/blast/holdout):
// each deal's buyer list + its owner's do-not-contact firms, matched against
// the SuperSend roster. A held-out buyer misses the whole email, so the
// per-deal counts show which deal is costing the reach.
const blastN = (v) => Number(v || 0).toLocaleString();
const blastPlural = (v, one, many) => `${blastN(v)} ${Number(v) === 1 ? one : (many || one + 's')}`;

const BlastHoldoutLine = ({ h, err }) => {
  const [copied, setCopied] = React.useState(false);
  if (err) return <div style={{ fontSize: 12, color: 'var(--text-3)' }} title={err}>Held-out count unavailable</div>;
  if (!h) return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Counting held-out buyers…</div>;
  const held = h.held_out || {};
  const kinds = held.by_kind || {};
  const deals = (held.by_company || []).filter((c) => c.total > 0);
  const copy = async () => {
    try { await navigator.clipboard.writeText((h.emails || []).join('\n')); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch (_) { /* clipboard blocked — the count still stands */ }
  };
  const muted = { color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' };
  const asOf = h.roster_as_of ? `SuperSend roster read ${blastWhen(h.roster_as_of)}` : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-2)' }}>
      <span title={h.source === 'atlas' ? 'Atlas contacts only: the SuperSend roster could not be read' : asOf}>
        <span style={{ fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>Held out {blastN(held.total)}</span>
        {h.roster != null && <span style={muted}> of {blastN(h.roster)} buyers</span>}
      </span>
      {held.total > 0 && h.still_on != null && (
        <span style={muted} title="Already off: taken off the campaign by an earlier push. Taken off at push: still on the campaign until you push.">
          {blastN(h.already_off)} already off · {blastN(h.still_on)} taken off at push
        </span>
      )}
      {held.total > 0 && (
        <span style={muted}>
          {blastN(kinds.buyer_list)} buyer list · {blastN(kinds.dnc)} do-not-contact
        </span>
      )}
      {deals.map((c) => (
        <span key={c.company_id} title={`${blastN(c.buyer_list)} on its buyer list · ${blastN(c.dnc)} do-not-contact`}
          style={{ fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, padding: '0 5px', color: 'var(--text-2)' }}>
          {c.name || 'Deal'} <span style={muted}>{blastN(c.total)}</span>
        </span>
      ))}
      {h.freed > 0 && (
        <span style={muted} title="Taken off for an earlier send and not held out by this one: the push puts them back on the campaign">
          {blastN(h.freed)} back on at push
        </span>
      )}
      {(h.emails || []).length > 0 && (
        <BlastBtn onClick={copy} title="For the copy/paste path: exclude these in SuperSend before sending">
          {copied ? 'Copied' : 'Copy held-out emails'}
        </BlastBtn>
      )}
    </div>
  );
};

const blastBanner = (tone, key, children) => {
  const fg = tone === 'rose' ? 'var(--rose-fg, #b42318)' : 'var(--amber-fg, #93540a)';
  return (
    <div key={key} role={tone === 'rose' ? 'alert' : 'status'}
      style={{ fontSize: 12, color: fg, border: `1px solid ${fg}`, borderRadius: 6, padding: '6px 10px', background: 'var(--surface)' }}>
      {children}
    </div>
  );
};

// The push's hold-out job (GET /api/blast/holdout/job), polled while it
// runs. Anyone SuperSend refused to take off gets the email, so that state
// blocks — it says so until a push clears it.
const BlastHoldoutJob = ({ state }) => {
  if (!state) return null;
  const j = state.job;
  const out = [];
  const mine = j && (j.status === 'claimed' || (state.open_batch_id && j.batch_id === state.open_batch_id));
  if (mine) {
    const progress = `${blastN((j.done || 0) + (j.failed || 0))} of ${blastN(j.total)}`;
    if (j.stale) {
      out.push(blastBanner('rose', 'stale', `Taking buyers off the campaign stopped partway (${progress}). Push again before sending.`));
    } else if (j.status === 'claimed' || j.status === 'running') {
      out.push(blastBanner('amber', 'run', `Taking buyers off the campaign… ${progress}. Don't press send in SuperSend yet.`));
    } else if (j.status === 'failed') {
      out.push(blastBanner('rose', 'failed', `Taking buyers off the campaign failed${j.detail && j.detail.error ? `: ${j.detail.error}` : ''}. Push again before sending.`));
    } else if (j.still_on > 0) {
      out.push(blastBanner('rose', 'still', (
        <React.Fragment>
          <div style={{ fontWeight: 600 }}>
            {blastPlural(j.still_on, 'buyer')} still on the campaign: they'll get this email. Push again to retry, or remove them in SuperSend.
          </div>
          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: 'pointer' }}>Show addresses</summary>
            <div style={{ marginTop: 4, color: 'var(--text-2)', wordBreak: 'break-all' }}>
              {(j.errors || []).map((x) => <div key={x.email} title={x.error}>{x.email}</div>)}
              {j.still_on > (j.errors || []).length && <div>…</div>}
            </div>
          </details>
        </React.Fragment>
      )));
    }
  }
  if (mine && j.status === 'done' && j.not_back > 0) {
    out.push(blastBanner('amber', 'notback', (
      <React.Fragment>
        <div style={{ fontWeight: 600 }}>
          {blastPlural(j.not_back, 'buyer')} couldn't be put back on the campaign: they'll miss this email.
        </div>
        <details style={{ marginTop: 4 }}>
          <summary style={{ cursor: 'pointer' }}>Show addresses</summary>
          <div style={{ marginTop: 4, color: 'var(--text-2)', wordBreak: 'break-all' }}>
            {(j.not_back_rows || []).map((x) => <div key={x.email} title={x.error}>{x.email}</div>)}
            {j.not_back > (j.not_back_rows || []).length && <div>…</div>}
          </div>
        </details>
      </React.Fragment>
    )));
  }
  if ((state.needs_repush || []).length) {
    out.push(blastBanner('amber', 'repush',
      `Changed after the push: ${state.needs_repush.map((x) => `${x.name || 'a deal'} (${x.change})`).join(' · ')}. Push again before sending.`));
  }
  return out.length ? <React.Fragment>{out}</React.Fragment> : null;
};

const BlastEmailTab = ({ listKey, counts, latestBatch, onSent }) => {
  const [email, setEmail] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [busy, setBusy] = React.useState(null);   // 'copy' | 'push' | 'sent'
  const [notice, setNotice] = React.useState(null);
  const [holdout, setHoldout] = React.useState(null);
  const [holdoutErr, setHoldoutErr] = React.useState(null);
  const [job, setJob] = React.useState(null);
  const [hoTick, setHoTick] = React.useState(0);
  const jobRunning = !!(job && job.job && ['claimed', 'running'].includes(job.job.status) && !job.job.stale);

  const loadJob = React.useCallback(() => {
    if (!window.AtlasApi.blastHoldoutJob) return Promise.resolve(null);
    return window.AtlasApi.blastHoldoutJob(listKey).then((r) => { setJob(r); return r; }).catch(() => null);
  }, [listKey]);
  React.useEffect(() => { setJob(null); loadJob(); }, [loadJob, counts?.queued]);
  // Poll while the push is taking people off; recount once it stops.
  React.useEffect(() => {
    if (!jobRunning) return undefined;
    const t = setTimeout(() => {
      loadJob().then((r) => {
        const st = r && r.job;
        if (st && !['claimed', 'running'].includes(st.status)) setHoTick((x) => x + 1);
      });
    }, 2500);
    return () => clearTimeout(t);
  }, [jobRunning, job, loadJob]);

  React.useEffect(() => {
    let live = true;
    setEmail(null); setErr(null); setNotice(null);
    window.AtlasApi.blastEmail(listKey)
      .then((r) => { if (live) setEmail(r); })
      .catch((e) => { if (live) setErr(String(e?.message || e)); });
    return () => { live = false; };
  }, [listKey, counts?.queued]);

  React.useEffect(() => {
    let live = true;
    setHoldout(null); setHoldoutErr(null);
    if (!window.AtlasApi.blastHoldout) return undefined;
    window.AtlasApi.blastHoldout(listKey)
      .then((r) => { if (live) { setHoldout(r); setHoldoutErr(r && r.error ? r.error : null); } })
      .catch((e) => { if (live) setHoldoutErr(String(e?.message || e)); });
    return () => { live = false; };
  }, [listKey, counts?.queued, hoTick]);

  const copyHtml = async () => {
    if (!email?.html) return;
    setBusy('copy');
    try {
      await navigator.clipboard.writeText(email.html);
      setNotice('HTML copied — paste it into the SuperSend campaign editor.');
    } catch (_) {
      setNotice('Copy failed — select the preview and copy manually.');
    } finally { setBusy(null); }
  };

  const push = async () => {
    setBusy('push'); setNotice(null); setErr(null);
    try {
      const r = await window.AtlasApi.blastPush(listKey);
      const ho = r.holdout || {};
      setNotice(`Pushed ${r.company_count} companies into the SuperSend campaign at ${blastWhen(r.pushed_at)}. ` +
        `Taking ${blastPlural(ho.to_remove, 'buyer')} off it before you press send.`);
      if (ho.campaign_live) {
        setErr('The campaign is live in SuperSend: buyers still on it can be emailed before they are taken off.');
      }
      loadJob();
      onSent && onSent();
    } catch (e) {
      setNotice(null); setErr(String(e?.message || e));
    } finally { setBusy(null); }
  };

  const markSent = async () => {
    if (!window.confirm(`Mark the ${listKey.toUpperCase()} blast as sent? ` +
      'This records the batch and advances the deals to the blast stage.')) return;
    setBusy('sent'); setErr(null);
    try {
      const r = await window.AtlasApi.blastMarkSent(listKey);
      setNotice(`Recorded: ${r.company_count} companies sent.`);
      onSent && onSent();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally { setBusy(null); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      {latestBatch?.status === 'pushed' && (
        <div style={{ fontSize: 12, color: 'var(--amber-fg, #93540a)', border: '1px solid var(--amber-fg, #93540a)',
                      borderRadius: 6, padding: '6px 10px', background: 'var(--surface)' }}>
          Pushed to SuperSend {blastWhen(latestBatch.pushed_at)} ({latestBatch.company_count} companies) —
          awaiting the send. Open it in History and mark that blast as sent.
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <BlastBtn onClick={copyHtml} disabled={!email?.html || busy === 'copy'}>Copy HTML</BlastBtn>
        <BlastBtn tone="primary" onClick={push} disabled={!email?.html || busy === 'push' || jobRunning}
          title={jobRunning ? 'Still taking buyers off from the last push' : 'Write this email into the SuperSend broadcast campaign'}>
          {busy === 'push' ? 'Pushing…' : 'Push to SuperSend'}
        </BlastBtn>
        {latestBatch?.status !== 'pushed' && (
          <BlastBtn onClick={markSent} disabled={busy === 'sent' || !email?.html}
            title="Record that this blast went out (copy/paste path)">Mark as sent</BlastBtn>
        )}
        {email?.subject && (
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
            <span style={{ color: 'var(--text-3)' }}>Subject:</span> {email.subject}
          </span>
        )}
      </div>
      <BlastHoldoutJob state={job} />
      {email?.html && <BlastHoldoutLine h={holdout} err={holdoutErr} />}
      {notice && <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{notice}</div>}
      {err && <div style={{ fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{err}</div>}
      {email?.blocked?.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
          <span style={{ color: 'var(--rose-fg, #b42318)', fontWeight: 600 }}>
            Not in this email:</span>{' '}
          {email.blocked.map((b) => `${b.name || b.company_id} (${b.missing.join(', ')})`).join(' · ')}
        </div>
      )}
      {email?.html ? (
        <iframe title="blast preview" sandbox="" srcDoc={email.html}
          style={{ flex: 1, minHeight: 380, width: '100%', border: '1px solid var(--border)',
                   borderRadius: 8, background: '#fff' }} />
      ) : (
        <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-3)' }}>
          {err ? '' : email ? 'Nothing ready to send — every queued company is missing required fields.' : 'Building…'}
        </div>
      )}
    </div>
  );
};

// ── History tab ─────────────────────────────────────────────────────────────

const BlastHistoryTab = ({ onOpenCompany, onChanged }) => {
  const [rows, setRows] = React.useState(null);
  const [open, setOpen] = React.useState(null);   // batch detail
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const load = React.useCallback(() => {
    window.AtlasApi.blastBatches().then(setRows).catch(() => setRows([]));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const markBatchSent = async () => {
    if (!open) return;
    if (!window.confirm(`Mark this ${open.list_type.toUpperCase()} blast (${open.company_count} companies) as sent? ` +
      'This records the send and advances the deals to the blast stage.')) return;
    setBusy(true); setErr(null);
    try {
      await window.AtlasApi.blastMarkSent(open.list_type, open.id);
      const fresh = await window.AtlasApi.blastBatch(open.id);
      setOpen(fresh);
      load();
      onChanged && onChanged();
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  if (rows == null) return <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>;
  if (!rows.length) return <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-3)' }}>No blasts recorded yet.</div>;

  return (
    <div style={{ display: 'flex', gap: 12, minHeight: 0, flex: 1 }}>
      <div style={{ width: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((b) => (
          <button key={b.id} onClick={() => window.AtlasApi.blastBatch(b.id).then(setOpen)} style={{
            textAlign: 'left', border: '1px solid ' + (open?.id === b.id ? 'var(--accent-text)' : 'var(--border)'),
            borderRadius: 8, background: 'var(--surface)', padding: '8px 10px', cursor: 'pointer' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>
              {b.list_type.toUpperCase()} · {b.company_count} companies
              {b.sent_to > 0 && <span style={{ color: 'var(--accent-text)', fontWeight: 500 }}> · sent to {b.sent_to.toLocaleString()}</span>}
              {b.status === 'pushed' && <span style={{ color: 'var(--amber-fg, #93540a)', fontWeight: 500 }}> · awaiting send</span>}
              {b.held_out_count > 0 && <span style={{ color: 'var(--text-3)', fontWeight: 500 }}> · held out {blastN(b.held_out_count)}</span>}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
              {b.sent_at ? `sent ${blastWhen(b.sent_at)} (${b.sent_via})` : `pushed ${blastWhen(b.pushed_at)}`}
              {b.created_by ? ` · ${b.created_by}` : ''}
            </div>
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {open ? (
          <React.Fragment>
            {open.status === 'pushed' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
                            color: 'var(--amber-fg, #93540a)', border: '1px solid var(--amber-fg, #93540a)',
                            borderRadius: 6, padding: '6px 10px', background: 'var(--surface)' }}>
                <span style={{ flex: 1 }}>
                  This blast is in SuperSend awaiting confirmation.
                </span>
                <BlastBtn tone="primary" onClick={markBatchSent} disabled={busy}
                  title="Confirm THIS batch only — records the send and advances its deals">
                  {busy ? 'Recording…' : `Mark this blast as sent (${open.company_count} companies)`}
                </BlastBtn>
              </div>
            )}
            {err && <div style={{ fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{err}</div>}
            {open.status === 'pushed' && <BlastHoldoutJob state={{ needs_repush: open.needs_repush }} />}
            {open.held_out && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--text-3)' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums' }}>
                  Held out {blastN(open.held_out.total)}
                </span>
                {open.held_out.total > 0 && (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {blastN((open.held_out.by_kind || {}).buyer_list)} buyer list · {blastN((open.held_out.by_kind || {}).dnc)} do-not-contact
                  </span>
                )}
                {(open.held_out.by_company || []).map((c) => (
                  <span key={c.company_id} style={{ fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, padding: '0 5px', color: 'var(--text-2)' }}>
                    {c.name || 'Deal'} <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)' }}>{blastN(c.total)}</span>
                  </span>
                ))}
                {open.held_out.still_on > 0 && (
                  <span style={{ color: 'var(--rose-fg, #b42318)' }} title="SuperSend refused to take them off">
                    {blastPlural(open.held_out.still_on, 'buyer')} left on
                  </span>
                )}
                {open.held_out.not_put_back > 0 && (
                  <span title="Taken off for an earlier send, not held out by this one, and not back on yet">{blastN(open.held_out.not_put_back)} not put back</span>
                )}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(open.companies || []).map((c) => (
                <button key={c.company_id} onClick={() => onOpenCompany(c.company_id)} style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: 5,
                  padding: '2px 7px', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 11.5 }}>
                  {c.name}
                </button>
              ))}
            </div>
            <iframe title="batch snapshot" sandbox="" srcDoc={open.html}
              style={{ flex: 1, minHeight: 380, width: '100%', border: '1px solid var(--border)',
                       borderRadius: 8, background: '#fff' }} />
          </React.Fragment>
        ) : (
          <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-3)' }}>Select a blast to see exactly what went out.</div>
        )}
      </div>
    </div>
  );
};

// ── Settings tab ────────────────────────────────────────────────────────────
// Binds each list to its SuperSend broadcast campaign. Stored in
// app_settings via PUT /api/blast/config (env vars are the fallback), so
// rebinding a campaign never needs a deploy.

const BlastCampaignPicker = ({ label, hint, value, campaigns, onChange }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-2)' }}>
    <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{label}</span>
    {campaigns ? (
      <window.Picker value={value || ''} onChange={onChange} emptyLabel="— not bound —" placeholder={label}
        style={{ maxWidth: 420 }} options={campaigns.map((c) => ({ value: c.id, label: c.name, hint: c.contact_count != null ? `${c.contact_count} contacts` : undefined }))} />
    ) : (
      <input value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder="SuperSend campaign id (uuid)" style={{
          fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
          background: 'var(--surface)', color: 'var(--text-1)', maxWidth: 420 }} />
    )}
    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{hint}</span>
  </label>
);

// Live-delivery health for the SuperSend webhook. Sent counts, batch
// auto-confirm and buyer reply notes all ride it, and it once sat dead for
// five days with no symptom anywhere in the UI — hence a permanent readout
// rather than a log line. Served by GET /api/blast/config (`webhook`).
const WEBHOOK_STATES = {
  live: { tone: 'var(--green-fg, #067647)', label: 'Delivering' },
  stale: { tone: 'var(--amber-fg, #93540a)', label: 'No deliveries recently' },
  rejecting: { tone: 'var(--rose-fg, #b42318)', label: 'Deliveries rejected' },
  never: { tone: 'var(--amber-fg, #93540a)', label: 'Never delivered' },
  unconfigured: { tone: 'var(--text-3)', label: 'Not configured' },
};

const WEBHOOK_HINTS = {
  stale: 'Check the webhook in the SuperSend dashboard.',
  rejecting: 'SuperSend does not retry a rejected delivery. Fix the mismatch below.',
  never: 'The webhook has never reached Atlas. It must exist in the SuperSend dashboard with "apply to all campaigns" on, pointed at /api/marketing/webhooks/supersend.',
  unconfigured: 'SUPERSEND_WEBHOOK_SECRET is not set on the server, so every delivery is refused with a 503.',
};

const BlastWebhookHealth = ({ wh }) => {
  if (!wh) return null;
  const state = WEBHOOK_STATES[wh.status] || WEBHOOK_STATES.unconfigured;
  const when = (iso) => (iso ? new Date(iso).toLocaleString() : null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '10px 12px',
      border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2, transparent)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 8, background: state.tone, flex: '0 0 auto' }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>
          SuperSend webhook — {state.label}
        </span>
      </div>
      {WEBHOOK_HINTS[wh.status] && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{WEBHOOK_HINTS[wh.status]}</span>}
      {wh.last_received_at && (
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
          Last delivery {when(wh.last_received_at)} ({wh.hours_since_received}h ago)
          {wh.deliveries ? ` · ${wh.deliveries} total` : ''}
        </span>
      )}
      {wh.last_rejected_at && (
        <span style={{ fontSize: 11, color: 'var(--rose-fg, #b42318)' }}>
          Last rejected {when(wh.last_rejected_at)} — {wh.last_reject_reason}
          {wh.rejects_since_ok > 1 ? ` (${wh.rejects_since_ok} since the last good delivery)` : ''}
        </span>
      )}
    </div>
  );
};

const BlastSettingsTab = () => {
  const [cfg, setCfg] = React.useState(null);
  const [campaigns, setCampaigns] = React.useState(null); // null = picker unavailable
  const [pb, setPb] = React.useState('');
  const [peg, setPeg] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState(null);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    window.AtlasApi.blastConfig().then((c) => {
      setCfg(c); setPb(c.pb_campaign_id || ''); setPeg(c.peg_campaign_id || '');
      if (c.api_configured) {
        window.AtlasApi.blastCampaigns().then(setCampaigns).catch(() => setCampaigns(null));
      }
    }).catch((e) => setErr(String(e?.message || e)));
  }, []);

  const save = async () => {
    setBusy(true); setErr(null); setNotice(null);
    try {
      const r = await window.AtlasApi.blastSaveConfig({ pb_campaign_id: pb, peg_campaign_id: peg });
      setCfg((c) => ({ ...c, ...r }));
      setNotice('Saved.');
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  if (!cfg && !err) return <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>;
  const srcLabel = (lt) => cfg?.source?.[lt] === 'env' ? ' (from server env)' : cfg?.source?.[lt] === 'db' ? '' : ' (not bound)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 560, paddingTop: 4 }}>
      {cfg && !cfg.api_configured && (
        <div style={{ fontSize: 12, color: 'var(--amber-fg, #93540a)' }}>
          SUPERSEND_API_KEY / SUPERSEND_TEAM_ID are not set on the server — the campaign picker,
          Push to SuperSend, and the sent auto-detect are unavailable until they are.
        </div>
      )}
      <BlastWebhookHealth wh={cfg?.webhook} />
      <BlastCampaignPicker label={`PB Broadcast campaign${srcLabel('pb')}`}
        hint="The SuperSend campaign whose email body the PB push replaces."
        value={pb} campaigns={campaigns} onChange={setPb} />
      <BlastCampaignPicker label={`PEG Broadcast campaign${srcLabel('peg')}`}
        hint="Same, for the PEG blast."
        value={peg} campaigns={campaigns} onChange={setPeg} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <BlastBtn tone="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</BlastBtn>
        {notice && <span style={{ fontSize: 12, color: 'var(--green-fg, #067647)' }}>{notice}</span>}
        {err && <span style={{ fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{err}</span>}
      </div>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

const BlastView = ({ setCompanyId }) => {
  const [tab, setTab] = React.useState('pb');           // pb | peg | history
  const [subTab, setSubTab] = React.useState('queue');  // queue | email
  const [board, setBoard] = React.useState(null);
  const [err, setErr] = React.useState(null);

  const refresh = React.useCallback(() => {
    window.AtlasApi.blastBoard()
      .then((b) => { setBoard(b); setErr(null); })
      .catch((e) => setErr(String(e?.message || e)));
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  const onRemove = async (entry) => {
    const reason = window.prompt(`Remove ${entry.company?.name || 'this company'} from the ${entry.list_type.toUpperCase()} queue — why?`);
    if (reason == null) return;
    try {
      await window.AtlasApi.blastRemoveEntry(entry.entry_id, reason);
      refresh();
    } catch (e) { window.pushToast({ type: 'error', title: 'Could not remove from the queue', body: String(e?.message || e) }); }
  };

  const openCompany = (id) => setCompanyId && setCompanyId(id);
  const data = tab !== 'history' ? board?.[tab] : null;
  const tabBtn = (key, label, badge) => (
    <button key={key} onClick={() => { setTab(key); setSubTab('queue'); }} style={{
      fontSize: 12.5, fontWeight: tab === key ? 600 : 400, padding: '6px 12px', cursor: 'pointer',
      border: 'none', borderBottom: '2px solid ' + (tab === key ? 'var(--accent-text)' : 'transparent'),
      background: 'none', color: tab === key ? 'var(--text-1)' : 'var(--text-3)' }}>
      {label}{badge != null && badge > 0 ? ` (${badge})` : ''}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '14px 18px', gap: 12, minHeight: 0 }}>
      <div>
        <h1 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-1)' }}>Blast</h1>
      </div>
      {err && <div style={{ fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{err}</div>}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
        {BLAST_LISTS.map((l) => tabBtn(l.key, l.label, board?.[l.key]?.counts?.queued))}
        {tabBtn('history', 'History')}
        {tabBtn('settings', 'Settings')}
      </div>

      {tab === 'settings' ? (
        <BlastSettingsTab />
      ) : tab === 'history' ? (
        <BlastHistoryTab onOpenCompany={openCompany} onChanged={refresh} />
      ) : data ? (
        <React.Fragment>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <BlastStat label="queued" value={data.counts.queued} />
            <BlastStat label="ready" value={data.counts.ready} tone="var(--green-fg, #067647)" />
            <BlastStat label="blocked" value={data.counts.blocked}
                       tone={data.counts.blocked ? 'var(--rose-fg, #b42318)' : undefined} />
            <BlastStat label="in SuperSend" value={data.counts.in_supersend ?? 0}
                       tone={data.counts.in_supersend ? 'var(--accent-text)' : undefined} />
            <BlastStat label="last sent"
                       value={data.latest_batch?.sent_at ? blastWhen(data.latest_batch.sent_at) : '—'} />
            <BlastStat label="sent to"
                       value={data.latest_batch?.sent_to ? data.latest_batch.sent_to.toLocaleString() : '—'}
                       tone={data.latest_batch?.sent_to ? 'var(--accent-text)' : undefined} />
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 2 }}>
              {[['queue', 'Queue'], ['email', 'Email preview']].map(([k, label]) => (
                <button key={k} onClick={() => setSubTab(k)} style={{
                  fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                  border: '1px solid ' + (subTab === k ? 'var(--accent-text)' : 'var(--border)'),
                  background: subTab === k ? 'var(--surface)' : 'transparent',
                  color: subTab === k ? 'var(--text-1)' : 'var(--text-3)' }}>{label}</button>
              ))}
            </div>
          </div>
          {subTab === 'queue'
            ? <BlastQueueTab data={data} onOpenCompany={openCompany} onRemove={onRemove} />
            : <BlastEmailTab listKey={tab} counts={data.counts}
                latestBatch={data.latest_batch} onSent={refresh} />}
        </React.Fragment>
      ) : (
        <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-3)' }}>Loading…</div>
      )}
    </div>
  );
};

window.BlastView = BlastView;

// ── Record-page control ─────────────────────────────────────────────────────
// Sits next to the SyncBadge on the company page: shows queue/sent state and
// lets any editor queue the company for PB and/or PEG with the missing-field
// checklist visible before they commit.

const BlastQueueControl = ({ companyId }) => {
  const [state, setState] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [picked, setPicked] = React.useState({ pb: false, peg: false });
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const load = React.useCallback(() => {
    window.AtlasApi.blastCompanyState(companyId).then(setState).catch(() => setState(null));
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  if (!state) return null;
  const queuedLists = (state.queued || []).map((q) => q.list_type);

  const queue = async () => {
    const lists = Object.keys(picked).filter((k) => picked[k]);
    if (!lists.length) return;
    setBusy(true);
    try {
      const r = await window.AtlasApi.blastQueue({ company_id: companyId, lists });
      setResult(r);
      load();
    } catch (e) { window.pushToast({ type: 'error', title: 'Could not queue for blast', body: String(e?.message || e) }); }
    finally { setBusy(false); }
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {(state.queued || []).map((q) => (
        <span key={q.list_type} style={{ fontSize: 10, fontWeight: 600,
          color: q.pushed_at ? 'var(--accent-text)' : 'var(--amber-fg, #93540a)',
          border: '1px solid ' + (q.pushed_at ? 'var(--accent-text)' : 'var(--amber-fg, #93540a)'),
          borderRadius: 4, padding: '1px 5px' }}
          title={`Queued ${blastWhen(q.queued_at)}` + (q.pushed_at ? ` · in SuperSend since ${blastWhen(q.pushed_at)}` : '')}>
          {q.list_type.toUpperCase()} {q.pushed_at ? 'in SuperSend' : 'queued'}</span>
      ))}
      {Object.entries(state.last_sent || {}).filter(([lt]) => !queuedLists.includes(lt)).map(([lt, info]) => (
        <span key={lt} style={{ fontSize: 10, fontWeight: 600, color: 'var(--green-fg, #067647)',
          border: '1px solid var(--green-fg, #067647)', borderRadius: 4, padding: '1px 5px' }}
          title={`Queued ${blastWhen(info.queued_at)}` +
                 (info.pushed_at ? ` · in SuperSend ${blastWhen(info.pushed_at)}` : '') +
                 ` · email sent ${blastWhen(info.sent_at)} — permanent history`}>
          Sent on {lt.toUpperCase()} · {blastDate(info.sent_at)}</span>
      ))}
      <button onClick={() => { setOpen(!open); setResult(null); }} style={{
        fontSize: 11, padding: '2px 8px', borderRadius: 5, cursor: 'pointer',
        border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-2)' }}>
        Send on blast
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 400,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 12, width: 300 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8 }}>
            Queue for buyer blast
          </div>
          {BLAST_LISTS.map((l) => {
            const q = (state.queued || []).find((x) => x.list_type === l.key);
            const sent = (state.last_sent || {})[l.key];
            const locked = !!q || !!sent;
            const detail = sent
              ? [`queued ${blastWhen(sent.queued_at)}`,
                 sent.pushed_at && `in SuperSend ${blastWhen(sent.pushed_at)}`,
                 `email sent ${blastWhen(sent.sent_at)}`].filter(Boolean).join(' · ')
              : q
                ? [`queued ${blastWhen(q.queued_at)}${q.source === 'auto_pb' ? ' (auto after PB)' : ''}`,
                   q.pushed_at && `in SuperSend since ${blastWhen(q.pushed_at)}`].filter(Boolean).join(' · ')
                : null;
            return (
              <label key={l.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
                fontSize: 12, color: locked ? 'var(--text-4)' : 'var(--text-2)', padding: '4px 0', cursor: locked ? 'default' : 'pointer' }}>
                <input type="checkbox" disabled={locked} checked={locked || !!picked[l.key]}
                  onChange={(e) => setPicked({ ...picked, [l.key]: e.target.checked })} style={{ marginTop: 2 }} />
                <span>
                  <span style={{ fontWeight: 600 }}>{l.label}</span>
                  {sent ? ` — sent ${blastDate(sent.sent_at)}` : q ? (q.pushed_at ? ' — in SuperSend' : ' — queued') : ''}
                  {detail && <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{detail}</div>}
                </span>
              </label>
            );
          })}
          {result && (
            <div style={{ fontSize: 11, marginTop: 6 }}>
              {Object.entries(result.lists).map(([lt, r]) => (
                <div key={lt} style={{ color: r.already_sent ? 'var(--text-3)'
                    : r.missing?.length ? 'var(--amber-fg, #93540a)' : 'var(--green-fg, #067647)' }}>
                  {r.already_sent
                    ? `${lt.toUpperCase()}: already sent ${blastDate(r.already_sent)} — sent is permanent`
                    : `${lt.toUpperCase()}: queued${r.missing?.length
                        ? ` — will be EXCLUDED from the email until filled: ${r.missing.join(', ')}` : ' and ready'}`}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
            <BlastBtn onClick={() => setOpen(false)}>Close</BlastBtn>
            <BlastBtn tone="primary" onClick={queue}
              disabled={busy || !Object.values(picked).some(Boolean)}>Queue</BlastBtn>
          </div>
        </div>
      )}
    </span>
  );
};

window.BlastQueueControl = BlastQueueControl;
